// Видеоконференции: состояние (кто куда приглашён) + сам звонок.
// Видео идёт напрямую между браузерами (WebRTC, каждый с каждым), сервер только
// передаёт служебные сообщения через сокет. Голос каждый пишет у себя и шлёт кусками.
import { useEffect, useState } from 'react';
import axios from 'axios';
import socketService from './socket';
import SERVER_CONFIG from './config';

const http = axios.create({ baseURL: `${SERVER_CONFIG.apiUrl}/conference` });
http.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const errText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

// ─── Общее состояние + подписка для React ─────────────────────────────────

const store = {
  canCreate: false,
  channelId: null,
  active: [],    // идущие конференции, куда меня пригласили
  call: null,    // текущий звонок (ConferenceCall) или null
  version: 0,
};
const listeners = new Set();

function notify() {
  store.version += 1;
  listeners.forEach(fn => fn(store.version));
}

// Для диагностики из консоли браузера
window.__conference = store;

export function useConference() {
  const [, setV] = useState(0);
  useEffect(() => {
    listeners.add(setV);
    return () => listeners.delete(setV);
  }, []);
  return store;
}

export async function loadConferenceState() {
  try {
    const { data } = await http.get('/state');
    store.canCreate = data.canCreate;
    store.channelId = data.channelId;
    store.active = data.active || [];
    notify();
  } catch (err) {
    console.warn('[Conference] state:', errText(err));
  }
}

export const conferenceApi = {
  candidates: () => http.get('/candidates').then(r => r.data),
  start: (participantIds, recording) => http.post('/', { participantIds, recording }).then(r => r.data),
  history: () => http.get('/history').then(r => r.data),
  transcript: id => http.get(`/${id}/transcript`).then(r => r.data),
  audioUrl: id => `${SERVER_CONFIG.apiUrl}/conference/${id}/audio?token=${encodeURIComponent(localStorage.getItem('token') || '')}`,
  txtUrl: id => `${SERVER_CONFIG.apiUrl}/conference/${id}/transcript.txt?token=${encodeURIComponent(localStorage.getItem('token') || '')}`,
};

export async function joinConference(conferenceId) {
  if (store.call) {
    if (store.call.confId === conferenceId) return;
    await store.call.leave();
  }
  const call = new ConferenceCall(conferenceId);
  store.call = call;
  notify();
  await call.start();
}

export async function startConference(participantIds, recording) {
  const res = await conferenceApi.start(participantIds, recording);
  await loadConferenceState();
  await joinConference(res.id);
}

// ─── События сокета ────────────────────────────────────────────────────────

let socketBound = false;

export function bindConferenceSocket() {
  if (socketBound) return;
  socketBound = true;

  socketService.on('conf:invite', (summary) => {
    store.active = [...store.active.filter(c => c.id !== summary.id), summary];
    if (!store.call && navigator.vibrate) navigator.vibrate([300, 150, 300]);
    notify();
  });
  socketService.on('conf:ended', ({ conferenceId }) => {
    store.active = store.active.filter(c => c.id !== conferenceId);
    if (store.call?.confId === conferenceId) store.call.handleEnded();
    notify();
  });
  socketService.on('conf:presence', ({ conferenceId, online }) => {
    store.active = store.active.map(c => (c.id === conferenceId ? { ...c, online } : c));
    notify();
  });
  const forward = (event, method) => socketService.on(event, (data) => {
    if (store.call && store.call.confId === data.conferenceId) store.call[method](data);
  });
  forward('conf:peer-joined', 'onPeerJoined');
  forward('conf:peer-left', 'onPeerLeft');
  forward('conf:signal', 'onSignal');
  forward('conf:state', 'onPeerState');
  forward('conf:recording', 'onRecording');

  // После обрыва связи: обновить приглашения и вернуться в идущий звонок
  socketService.addConnectListener(() => {
    loadConferenceState();
    if (store.call) store.call.rejoin();
  });
}

// ─── Звонок ────────────────────────────────────────────────────────────────

const VIDEO_CONSTRAINTS = {
  width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 20, max: 24 },
};
const AUDIO_CONSTRAINTS = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
const VIDEO_MAX_BITRATE = 600000;

function randomId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const sleep = ms => new Promise(r => setTimeout(r, ms));

export class ConferenceCall {
  constructor(confId) {
    this.confId = confId;
    this.peerId = randomId();
    this.status = 'connecting';   // connecting | live | ended | error
    this.error = null;
    this.endReason = null;
    this.conference = null;
    this.isHost = false;
    this.recording = false;
    this.localStream = null;
    this.audioOn = true;
    this.videoOn = true;
    this.facing = 'user';
    this.peers = new Map();       // peerId -> entry
    this.iceServers = [];
    this.recorder = null;
    this.wakeLock = null;
    this.startedAt = Date.now();
    this.onVisibility = () => {
      if (document.visibilityState === 'visible' && this.status === 'live') this.requestWakeLock();
    };
  }

  // ── вход ──
  async start() {
    try {
      this.localStream = await this.openMedia();
    } catch (err) {
      return this.fail('Нет доступа к камере и микрофону. Разрешите их в настройках браузера и попробуйте снова.');
    }
    const resp = await this.join();
    if (!resp) return;
    if (!resp.ok) return this.fail(resp.error || 'Не удалось войти в конференцию');
    this.applyJoin(resp);
    this.status = 'live';
    this.requestWakeLock();
    document.addEventListener('visibilitychange', this.onVisibility);
    if (this.recording) this.startRecorder();
    notify();
  }

  async openMedia() {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONSTRAINTS, video: { ...VIDEO_CONSTRAINTS, facingMode: this.facing },
      });
    } catch (err) {
      // Камера занята или её нет — входим хотя бы с голосом
      const s = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
      this.videoOn = false;
      return s;
    }
  }

  async join() {
    let lastErr;
    for (let i = 0; i < 4; i += 1) {
      if (this.status === 'ended') return null;
      try {
        return await socketService.emitWithAck('conf:join', {
          conferenceId: this.confId, peerId: this.peerId, audio: this.audioOn, video: this.videoOn,
        });
      } catch (err) {
        lastErr = err;
        await sleep(1500);
      }
    }
    this.fail(`Нет связи с сервером чата (${lastErr?.message || 'ошибка'})`);
    return null;
  }

  applyJoin(resp) {
    this.conference = resp.conference;
    this.isHost = resp.isHost;
    this.recording = resp.recording;
    this.iceServers = resp.iceServers || [];
    for (const p of resp.peers) {
      const existing = this.peers.get(p.peerId);
      if (existing) Object.assign(existing.info, p);
      else this.createPeer(p);
    }
  }

  async rejoin() {
    if (this.status !== 'live') return;
    const resp = await this.join();
    if (!resp) return;
    if (!resp.ok) return this.handleEnded();
    if (!resp.resumed) {
      // Сервер нас забыл (перезапуск) — пересобираем соединения заново
      for (const id of [...this.peers.keys()]) this.closePeer(id);
    }
    this.applyJoin(resp);
    if (this.recording && !this.recorder) this.startRecorder();
    if (!this.recording && this.recorder) this.stopRecorder();
    notify();
  }

  // ── соединения с участниками (perfect negotiation) ──
  createPeer(info) {
    // localStorage.confRelay = '1' — только через TURN (проверка «как с мобильного интернета»)
    const relayOnly = localStorage.getItem('confRelay') === '1';
    const pc = new RTCPeerConnection({ iceServers: this.iceServers, iceTransportPolicy: relayOnly ? 'relay' : 'all' });
    const entry = {
      pc,
      info: { ...info },
      stream: null,
      polite: this.peerId < info.peerId,
      makingOffer: false,
      ignoreOffer: false,
      queue: Promise.resolve(),
      state: 'connecting',
      restartTimer: null,
    };
    this.peers.set(info.peerId, entry);

    const tracks = this.localStream ? this.localStream.getTracks() : [];
    tracks.forEach(t => pc.addTrack(t, this.localStream));
    // Без своей камеры всё равно хотим видеть других
    if (!tracks.some(t => t.kind === 'video')) pc.addTransceiver('video', { direction: 'recvonly' });
    if (!tracks.some(t => t.kind === 'audio')) pc.addTransceiver('audio', { direction: 'recvonly' });

    pc.ontrack = (e) => {
      entry.stream = e.streams[0] || entry.stream || new MediaStream();
      if (!e.streams[0]) entry.stream.addTrack(e.track);
      notify();
    };
    pc.onicecandidate = ({ candidate }) => {
      if (candidate) this.signal(info.peerId, { candidate: candidate.toJSON() });
    };
    pc.onnegotiationneeded = async () => {
      try {
        entry.makingOffer = true;
        await this.setLocal(pc, 'offer');
        this.signal(info.peerId, { description: pc.localDescription.toJSON() });
      } catch (err) {
        console.warn('[Conference] offer:', err);
      } finally {
        entry.makingOffer = false;
      }
    };
    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      clearTimeout(entry.restartTimer);
      if (s === 'failed') {
        pc.restartIce();
      } else if (s === 'disconnected') {
        entry.restartTimer = setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected') pc.restartIce();
        }, 6000);
      }
    };
    pc.onconnectionstatechange = () => {
      entry.state = pc.connectionState;
      if (pc.connectionState === 'connected') this.limitBitrate(pc);
      notify();
    };
    notify();
    return entry;
  }

  async setLocal(pc, type) {
    try {
      await pc.setLocalDescription();
    } catch (err) {
      // Старые Safari не умеют setLocalDescription() без аргумента
      const desc = type === 'answer' ? await pc.createAnswer() : await pc.createOffer();
      await pc.setLocalDescription(desc);
    }
  }

  limitBitrate(pc) {
    pc.getSenders().forEach(sender => {
      if (sender.track?.kind !== 'video') return;
      const params = sender.getParameters();
      if (!params.encodings || !params.encodings.length) params.encodings = [{}];
      params.encodings[0].maxBitrate = VIDEO_MAX_BITRATE;
      sender.setParameters(params).catch(() => {});
    });
  }

  signal(to, data) {
    socketService.emit('conf:signal', { conferenceId: this.confId, from: this.peerId, to, data });
  }

  onSignal({ from, data }) {
    let entry = this.peers.get(from);
    // Предложение может прийти раньше, чем мы узнали об участнике
    if (!entry) entry = this.createPeer({ peerId: from, displayName: '…', audio: true, video: true });
    entry.queue = entry.queue.then(() => this.handleSignal(entry, data)).catch(err => {
      console.warn('[Conference] signal:', err);
    });
  }

  async handleSignal(entry, data) {
    const { pc } = entry;
    if (pc.signalingState === 'closed') return;
    if (data.description) {
      const desc = data.description;
      const collision = desc.type === 'offer' && (entry.makingOffer || pc.signalingState !== 'stable');
      entry.ignoreOffer = !entry.polite && collision;
      if (entry.ignoreOffer) return;
      await pc.setRemoteDescription(desc);
      if (desc.type === 'offer') {
        await this.setLocal(pc, 'answer');
        this.signal(entry.info.peerId, { description: pc.localDescription.toJSON() });
      }
    } else if (data.candidate) {
      try {
        await pc.addIceCandidate(data.candidate);
      } catch (err) {
        if (!entry.ignoreOffer) console.warn('[Conference] candidate:', err);
      }
    }
  }

  onPeerJoined(info) {
    if (this.status !== 'live' || info.peerId === this.peerId) return;
    if (this.peers.has(info.peerId)) this.closePeer(info.peerId);
    this.createPeer(info);
  }

  onPeerLeft({ peerId }) {
    this.closePeer(peerId);
    notify();
  }

  onPeerState({ peerId, audio, video }) {
    const entry = this.peers.get(peerId);
    if (entry) {
      entry.info.audio = audio;
      entry.info.video = video;
      notify();
    }
  }

  closePeer(peerId) {
    const entry = this.peers.get(peerId);
    if (!entry) return;
    clearTimeout(entry.restartTimer);
    try { entry.pc.close(); } catch (e) { /* уже закрыто */ }
    this.peers.delete(peerId);
  }

  // ── кнопки ──
  sendState() {
    socketService.emit('conf:state', {
      conferenceId: this.confId, peerId: this.peerId, audio: this.audioOn, video: this.videoOn,
    });
  }

  toggleAudio() {
    const t = this.localStream?.getAudioTracks()[0];
    if (!t) return;
    this.audioOn = !this.audioOn;
    t.enabled = this.audioOn;
    this.sendState();
    notify();
  }

  async toggleVideo() {
    let t = this.localStream?.getVideoTracks()[0];
    if (!t) {
      // Вошли без камеры — пробуем включить её сейчас
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { ...VIDEO_CONSTRAINTS, facingMode: this.facing } });
        t = s.getVideoTracks()[0];
        this.localStream.addTrack(t);
        this.peers.forEach(({ pc }) => {
          const tr = pc.getTransceivers().find(x => x.receiver.track?.kind === 'video' && !x.sender.track);
          if (tr) {
            tr.sender.replaceTrack(t);
            tr.direction = 'sendrecv';
          } else {
            pc.addTrack(t, this.localStream);
          }
        });
        this.videoOn = true;
      } catch (err) {
        alert('Камера недоступна');
        return;
      }
    } else {
      this.videoOn = !this.videoOn;
      t.enabled = this.videoOn;
    }
    this.sendState();
    notify();
  }

  async switchCamera() {
    const old = this.localStream?.getVideoTracks()[0];
    if (!old) return;
    const next = this.facing === 'user' ? 'environment' : 'user';
    // Многие телефоны не дают открыть вторую камеру, пока первая занята
    old.stop();
    let track;
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { ...VIDEO_CONSTRAINTS, facingMode: { exact: next } } });
      track = s.getVideoTracks()[0];
      this.facing = next;
    } catch (err) {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { ...VIDEO_CONSTRAINTS, facingMode: this.facing } });
        track = s.getVideoTracks()[0];
      } catch (err2) {
        this.localStream.removeTrack(old);
        this.videoOn = false;
        this.sendState();
        notify();
        alert('Не удалось переключить камеру');
        return;
      }
    }
    track.enabled = this.videoOn;
    this.localStream.removeTrack(old);
    this.localStream.addTrack(track);
    this.peers.forEach(({ pc }) => {
      const senders = pc.getSenders();
      const sender = senders.find(s => s.track === old) || senders.find(s => s.track?.kind === 'video');
      if (sender) sender.replaceTrack(track);
    });
    notify();
  }

  setRecording(on) {
    socketService.emit('conf:set-recording', { conferenceId: this.confId, on });
  }

  onRecording({ on }) {
    this.recording = on;
    if (on) this.startRecorder();
    else this.stopRecorder();
    notify();
  }

  endForAll() {
    socketService.emit('conf:end', { conferenceId: this.confId });
  }

  // ── запись своего голоса ──
  startRecorder() {
    if (this.recorder || this.status === 'ended') return;
    const track = this.localStream?.getAudioTracks()[0];
    if (!track || typeof MediaRecorder === 'undefined') return;
    this.recorder = new ChunkRecorder(this.confId, track);
    this.recorder.start().catch(err => {
      console.warn('[Conference] запись не запустилась:', err);
      this.recorder = null;
    });
  }

  stopRecorder() {
    const r = this.recorder;
    this.recorder = null;
    return r ? r.stop() : Promise.resolve();
  }

  async requestWakeLock() {
    try {
      if ('wakeLock' in navigator && !this.wakeLock) {
        this.wakeLock = await navigator.wakeLock.request('screen');
        this.wakeLock.addEventListener('release', () => { this.wakeLock = null; });
      }
    } catch (err) { /* не критично */ }
  }

  // ── выход ──
  async leave() {
    if (this.status === 'live') {
      socketService.emit('conf:leave', { conferenceId: this.confId, peerId: this.peerId });
    }
    await this.cleanup();
    this.dismiss();
  }

  handleEnded() {
    if (this.status === 'ended') return;
    this.endReason = 'Конференция завершена';
    this.cleanup();
    notify();
  }

  fail(message) {
    this.error = message;
    this.cleanup('error');
    notify();
  }

  async cleanup(status = 'ended') {
    this.status = status;
    document.removeEventListener('visibilitychange', this.onVisibility);
    for (const id of [...this.peers.keys()]) this.closePeer(id);
    const stopping = this.stopRecorder();
    if (this.localStream) this.localStream.getTracks().forEach(t => t.stop());
    if (this.wakeLock) this.wakeLock.release().catch(() => {});
    this.wakeLock = null;
    await stopping;
  }

  dismiss() {
    if (store.call === this) store.call = null;
    notify();
  }
}

// Пишет один микрофон кусками по 10 с и шлёт их на сервер по очереди с повторами
const RECORDER_MIMES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
const CHUNK_MS = 10000;

class ChunkRecorder {
  constructor(confId, track) {
    this.confId = confId;
    this.track = track;
    this.queue = [];
    this.seq = 0;
    this.segmentId = null;
    this.pumping = false;
    this.dead = false;
    this.mr = null;
  }

  async start() {
    const mime = RECORDER_MIMES.find(m => MediaRecorder.isTypeSupported(m)) || '';
    this.mr = new MediaRecorder(new MediaStream([this.track]), mime ? { mimeType: mime, audioBitsPerSecond: 32000 } : undefined);
    const realMime = this.mr.mimeType || mime || 'audio/webm';
    const { data } = await http.post(`/${this.confId}/segments`, { mime: realMime });
    this.segmentId = data.segmentId;
    this.mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        this.queue.push({ seq: this.seq, blob: e.data });
        this.seq += 1;
        this.pump();
      }
    };
    this.stopped = new Promise(resolve => { this.mr.onstop = resolve; });
    this.mr.start(CHUNK_MS);
  }

  async pump() {
    if (this.pumping || this.dead) return;
    this.pumping = true;
    let failures = 0;
    while (this.queue.length && !this.dead) {
      const item = this.queue[0];
      try {
        await http.post(`/${this.confId}/segments/${this.segmentId}/chunks?seq=${item.seq}`, item.blob, {
          headers: { 'Content-Type': 'application/octet-stream' }, timeout: 60000,
        });
        this.queue.shift();
        failures = 0;
      } catch (err) {
        const st = err?.response?.status;
        if (st === 410 || st === 404 || st === 403) { this.dead = true; break; }
        failures += 1;
        await sleep(Math.min(30000, 1000 * 2 ** Math.min(failures, 5)));
      }
    }
    this.pumping = false;
    if (this.drainResolve && !this.queue.length) this.drainResolve();
  }

  async stop() {
    if (!this.mr) return;
    if (this.mr.state !== 'inactive') {
      this.mr.stop();
      await Promise.race([this.stopped, sleep(3000)]);
    }
    if (!this.segmentId) return;
    // Дожидаемся отправки последних кусков, но не дольше 20 секунд
    const drained = new Promise(resolve => {
      this.drainResolve = resolve;
      if (!this.queue.length && !this.pumping) resolve();
    });
    this.pump();
    await Promise.race([drained, sleep(20000)]);
  }
}
